import { create } from 'zustand'
import {
  KnowledgeBase,
  KnowledgeDocument,
  KnowledgeVisibility,
  RetrievalHit,
  DocumentStatus,
} from '../types'
import { genId, nowIso } from '../utils/format'
import { useTaskStore } from './taskStore'

interface CreateKbInput {
  name: string
  description: string
  tags: string[]
  visibility: KnowledgeVisibility
  embeddingModelId?: string
  retrievalConfig: {
    topK: number
    scoreThreshold: number
    rerankEnabled?: boolean
  }
}

interface KnowledgeStore {
  knowledgeBases: KnowledgeBase[]
  documentsByKbId: Record<string, KnowledgeDocument[]>
  searchResults: Record<string, RetrievalHit[]>
  searching: Record<string, boolean>
  createKnowledgeBase: (input: CreateKbInput) => string
  updateKnowledgeBase: (id: string, patch: Partial<KnowledgeBase>) => void
  toggleKnowledgeBase: (id: string) => void
  deleteKnowledgeBase: (id: string) => boolean
  uploadDocument: (kbId: string, filename: string) => string
  importDocumentFromUrl: (kbId: string, url: string) => string
  deleteDocument: (kbId: string, documentId: string) => void
  reindexKnowledgeBase: (kbId: string) => void
  searchKnowledgeBase: (kbId: string, query: string) => Promise<void>
}

const kbSeed: KnowledgeBase[] = [
  {
    knowledgeBaseId: 'kb_product_docs',
    name: '产品文档库',
    description: '产品功能说明、FAQ 与使用手册，用于客服与售前 Agent',
    status: 'ready',
    visibility: 'team',
    tags: ['product', 'faq'],
    documentCount: 4,
    chunkCount: 318,
    embeddingModelId: 'model_claude_sonnet',
    retrievalConfig: { topK: 5, scoreThreshold: 0.7, rerankEnabled: true },
    usedByAgentCount: 1,
    createdAt: '2026-06-01T10:00:00Z',
    updatedAt: '2026-06-04T16:00:00Z',
  },
  {
    knowledgeBaseId: 'kb_ops_runbook',
    name: '运维手册库',
    description: '内部运维 Runbook 与故障处置流程',
    status: 'indexing',
    visibility: 'private',
    tags: ['ops', 'runbook'],
    documentCount: 2,
    chunkCount: 64,
    embeddingModelId: 'model_claude_sonnet',
    retrievalConfig: { topK: 4, scoreThreshold: 0.65 },
    usedByAgentCount: 0,
    createdAt: '2026-06-03T11:00:00Z',
    updatedAt: '2026-06-05T08:00:00Z',
  },
  {
    knowledgeBaseId: 'kb_legacy',
    name: '历史归档库',
    description: '已归档的旧版资料，暂不参与检索',
    status: 'disabled',
    visibility: 'private',
    tags: ['archive'],
    documentCount: 8,
    chunkCount: 920,
    retrievalConfig: { topK: 3, scoreThreshold: 0.75 },
    usedByAgentCount: 0,
    createdAt: '2026-05-20T09:00:00Z',
    updatedAt: '2026-05-28T14:00:00Z',
  },
]

const docSeed: Record<string, KnowledgeDocument[]> = {
  kb_product_docs: [
    {
      documentId: 'doc_1',
      knowledgeBaseId: 'kb_product_docs',
      filename: '产品功能说明.pdf',
      sourceType: 'upload',
      contentType: 'application/pdf',
      sizeBytes: 2_400_000,
      status: 'indexed',
      chunkCount: 142,
      createdAt: '2026-06-01T10:10:00Z',
      updatedAt: '2026-06-01T10:25:00Z',
    },
    {
      documentId: 'doc_2',
      knowledgeBaseId: 'kb_product_docs',
      filename: '常见问题 FAQ.md',
      sourceType: 'upload',
      contentType: 'text/markdown',
      sizeBytes: 86_000,
      status: 'indexed',
      chunkCount: 58,
      createdAt: '2026-06-02T09:00:00Z',
      updatedAt: '2026-06-02T09:05:00Z',
    },
    {
      documentId: 'doc_3',
      knowledgeBaseId: 'kb_product_docs',
      filename: 'pricing-page.html',
      sourceType: 'url',
      sourceUrl: 'https://example.com/pricing',
      contentType: 'text/html',
      sizeBytes: 120_000,
      status: 'indexed',
      chunkCount: 118,
      createdAt: '2026-06-03T15:00:00Z',
      updatedAt: '2026-06-03T15:12:00Z',
    },
    {
      documentId: 'doc_4',
      knowledgeBaseId: 'kb_product_docs',
      filename: '集成指南.docx',
      sourceType: 'upload',
      contentType:
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      sizeBytes: 540_000,
      status: 'failed',
      chunkCount: 0,
      error: '文档解析失败：不支持的内嵌对象',
      createdAt: '2026-06-04T16:00:00Z',
      updatedAt: '2026-06-04T16:02:00Z',
    },
  ],
  kb_ops_runbook: [
    {
      documentId: 'doc_5',
      knowledgeBaseId: 'kb_ops_runbook',
      filename: '故障处置流程.md',
      sourceType: 'upload',
      contentType: 'text/markdown',
      sizeBytes: 64_000,
      status: 'indexed',
      chunkCount: 64,
      createdAt: '2026-06-03T11:10:00Z',
      updatedAt: '2026-06-03T11:18:00Z',
    },
    {
      documentId: 'doc_6',
      knowledgeBaseId: 'kb_ops_runbook',
      filename: '监控告警手册.pdf',
      sourceType: 'upload',
      contentType: 'application/pdf',
      sizeBytes: 1_800_000,
      status: 'embedding',
      chunkCount: 0,
      createdAt: '2026-06-05T08:00:00Z',
      updatedAt: '2026-06-05T08:01:00Z',
    },
  ],
}

const DOC_FLOW: DocumentStatus[] = [
  'parsing',
  'chunking',
  'embedding',
  'indexed',
]

function updateDoc(
  kbId: string,
  docId: string,
  patch: Partial<KnowledgeDocument>,
) {
  useKnowledgeStore.setState((s) => ({
    documentsByKbId: {
      ...s.documentsByKbId,
      [kbId]: (s.documentsByKbId[kbId] || []).map((d) =>
        d.documentId === docId ? { ...d, ...patch, updatedAt: nowIso() } : d,
      ),
    },
  }))
}

function ingestDocument(kbId: string, doc: KnowledgeDocument, label: string) {
  useKnowledgeStore.setState((s) => ({
    documentsByKbId: {
      ...s.documentsByKbId,
      [kbId]: [doc, ...(s.documentsByKbId[kbId] || [])],
    },
  }))

  const finalChunks = 40 + Math.floor(Math.random() * 120)
  const taskId = useTaskStore
    .getState()
    .createTask('kb_doc_parse', label, [
      '文档解析',
      '文本清洗与切片',
      '向量化 embedding',
      '写入向量索引',
    ])
  // walk doc status alongside the task steps
  const stepDuration = 900
  DOC_FLOW.forEach((status, i) => {
    setTimeout(
      () =>
        updateDoc(kbId, doc.documentId, {
          status,
          chunkCount: status === 'indexed' ? finalChunks : 0,
        }),
      stepDuration * (i + 1),
    )
  })
  useTaskStore.getState().simulate(taskId, () => {
    useKnowledgeStore.setState((s) => ({
      knowledgeBases: s.knowledgeBases.map((kb) =>
        kb.knowledgeBaseId === kbId
          ? {
              ...kb,
              documentCount: kb.documentCount + 1,
              chunkCount: kb.chunkCount + finalChunks,
              status: kb.status === 'indexing' ? 'ready' : kb.status,
              updatedAt: nowIso(),
            }
          : kb,
      ),
    }))
  })
}

export const useKnowledgeStore = create<KnowledgeStore>((set, get) => ({
  knowledgeBases: kbSeed,
  documentsByKbId: docSeed,
  searchResults: {},
  searching: {},
  createKnowledgeBase: (input) => {
    const knowledgeBaseId = genId('kb')
    set((s) => ({
      knowledgeBases: [
        {
          knowledgeBaseId,
          name: input.name,
          description: input.description,
          status: 'indexing',
          visibility: input.visibility,
          tags: input.tags,
          documentCount: 0,
          chunkCount: 0,
          embeddingModelId: input.embeddingModelId,
          retrievalConfig: input.retrievalConfig,
          usedByAgentCount: 0,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        },
        ...s.knowledgeBases,
      ],
      documentsByKbId: { ...s.documentsByKbId, [knowledgeBaseId]: [] },
    }))
    const taskId = useTaskStore
      .getState()
      .createTask('kb_reindex', `初始化知识库：${input.name}`, [
        '创建知识库结构',
        '初始化向量索引',
      ])
    useTaskStore.getState().simulate(taskId, () => {
      set((s) => ({
        knowledgeBases: s.knowledgeBases.map((kb) =>
          kb.knowledgeBaseId === knowledgeBaseId
            ? { ...kb, status: 'ready', updatedAt: nowIso() }
            : kb,
        ),
      }))
    })
    return knowledgeBaseId
  },
  updateKnowledgeBase: (id, patch) =>
    set((s) => ({
      knowledgeBases: s.knowledgeBases.map((kb) =>
        kb.knowledgeBaseId === id
          ? { ...kb, ...patch, updatedAt: nowIso() }
          : kb,
      ),
    })),
  toggleKnowledgeBase: (id) =>
    set((s) => ({
      knowledgeBases: s.knowledgeBases.map((kb) =>
        kb.knowledgeBaseId === id
          ? {
              ...kb,
              status: kb.status === 'disabled' ? 'ready' : 'disabled',
              updatedAt: nowIso(),
            }
          : kb,
      ),
    })),
  deleteKnowledgeBase: (id) => {
    const kb = get().knowledgeBases.find((x) => x.knowledgeBaseId === id)
    if (kb && kb.usedByAgentCount > 0) return false
    set((s) => {
      const { [id]: _removed, ...rest } = s.documentsByKbId
      return {
        knowledgeBases: s.knowledgeBases.filter(
          (x) => x.knowledgeBaseId !== id,
        ),
        documentsByKbId: rest,
      }
    })
    return true
  },
  uploadDocument: (kbId, filename) => {
    const documentId = genId('doc')
    const doc: KnowledgeDocument = {
      documentId,
      knowledgeBaseId: kbId,
      filename,
      sourceType: 'upload',
      contentType: guessContentType(filename),
      sizeBytes: 100_000 + Math.floor(Math.random() * 2_000_000),
      status: 'uploaded',
      chunkCount: 0,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }
    ingestDocument(kbId, doc, `解析文档：${filename}`)
    return documentId
  },
  importDocumentFromUrl: (kbId, url) => {
    const documentId = genId('doc')
    const filename = url.split('/').pop() || 'imported-document'
    const doc: KnowledgeDocument = {
      documentId,
      knowledgeBaseId: kbId,
      filename,
      sourceType: 'url',
      sourceUrl: url,
      contentType: 'text/html',
      sizeBytes: 80_000 + Math.floor(Math.random() * 400_000),
      status: 'uploaded',
      chunkCount: 0,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }
    ingestDocument(kbId, doc, `导入并解析 URL：${filename}`)
    return documentId
  },
  deleteDocument: (kbId, documentId) =>
    set((s) => {
      const doc = (s.documentsByKbId[kbId] || []).find(
        (d) => d.documentId === documentId,
      )
      return {
        documentsByKbId: {
          ...s.documentsByKbId,
          [kbId]: (s.documentsByKbId[kbId] || []).filter(
            (d) => d.documentId !== documentId,
          ),
        },
        knowledgeBases: s.knowledgeBases.map((kb) =>
          kb.knowledgeBaseId === kbId
            ? {
                ...kb,
                documentCount: Math.max(0, kb.documentCount - 1),
                chunkCount: Math.max(0, kb.chunkCount - (doc?.chunkCount || 0)),
                updatedAt: nowIso(),
              }
            : kb,
        ),
      }
    }),
  reindexKnowledgeBase: (kbId) => {
    const kb = get().knowledgeBases.find((x) => x.knowledgeBaseId === kbId)
    if (!kb) return
    set((s) => ({
      knowledgeBases: s.knowledgeBases.map((x) =>
        x.knowledgeBaseId === kbId
          ? { ...x, status: 'indexing', updatedAt: nowIso() }
          : x,
      ),
    }))
    const taskId = useTaskStore
      .getState()
      .createTask('kb_reindex', `重建索引：${kb.name}`, [
        '重新切片',
        '重新向量化',
        '重建向量索引',
      ])
    useTaskStore.getState().simulate(taskId, () => {
      set((s) => ({
        knowledgeBases: s.knowledgeBases.map((x) =>
          x.knowledgeBaseId === kbId
            ? { ...x, status: 'ready', updatedAt: nowIso() }
            : x,
        ),
      }))
    })
  },
  searchKnowledgeBase: async (kbId, query) => {
    set((s) => ({ searching: { ...s.searching, [kbId]: true } }))
    await new Promise((r) => setTimeout(r, 900))
    const docs =
      get().documentsByKbId[kbId]?.filter((d) => d.status === 'indexed') || []
    const hits: RetrievalHit[] = (
      docs.length ? docs : [{ documentId: 'na', filename: '示例文档' } as any]
    )
      .slice(0, 4)
      .map((d, i) => ({
        chunkId: genId('chunk'),
        documentId: d.documentId,
        documentName: d.filename,
        snippet: `关于「${query}」的相关片段：${d.filename} 中提到的内容摘要 #${i + 1}。该片段与查询语义高度相关，可作为回答依据。`,
        score: Number((0.92 - i * 0.07).toFixed(2)),
      }))
    set((s) => ({
      searching: { ...s.searching, [kbId]: false },
      searchResults: { ...s.searchResults, [kbId]: hits },
    }))
  },
}))

function guessContentType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase()
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    md: 'text/markdown',
    txt: 'text/plain',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    csv: 'text/csv',
    html: 'text/html',
  }
  return map[ext || ''] || 'application/octet-stream'
}
